import React from 'react';
import useSheet from 'app/jss';
import classNames from 'classnames';
import { TweenMax, TimelineMax } from 'gsap';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import AnimatedGroupChild from 'app/components/commons/AnimatedGroupChild';
import CarouselRotating from 'app/components/friends/common/CarouselRotating';
import GroupContentsExisting from 'app/components/friends/GroupContentsExisting';
import Tappable from 'app/components/commons/Tappable';
import themeConfig from 'app/theme';

type Props = {
  classes: Object,
  onCardClick?: (id: number | string) => void,
  currentGroupId?: number | string,
  groups?: Array<FriendGroup>,
  animating?: boolean,
};

type State = {};

class GroupCardsDesktopOpen extends React.Component {
  props: Props;
  state: State = {};

  static defaultProps = {
    groups: [],
    currentGroupId: undefined,
    onCardClick: undefined,
    animating: false,
  };

  getLeaveAnimation = () => {
    if (!this.onGroupLeave) {
      this.onGroupLeave = (node) => {
        TweenMax.set(node, { zIndex: themeConfig.zIndex.fullscreen });
        return Leave.slideUpAndDisappear(0.5)(node);
      };
    }
    return this.onGroupLeave;
  }

  render() {
    const {
      classes,
      groups,
      currentGroupId,
      onCardClick,
      animating,
    } = this.props;

    const currentIndex = groups.findIndex(g => g.id === currentGroupId);
    return (
      <CarouselRotating
        currentIndex={currentIndex}
        onIndexChange={nextIndex => onCardClick(groups[nextIndex].id)}
        onLeave={this.getLeaveAnimation()}
        hideArrows={animating}
      >
        {groups.map((group) => {
          const isCurrent = group.id === currentGroupId;
          return (
            <AnimatedGroup
              className={classes.group}
              key={group.id}
            >
              {isCurrent && animating &&
                <AnimatedGroupChild
                  onEnter={Enter.appear(0.2)}
                  onLeave={Leave.disappear(0.2)}
                  key="title"
                >
                  <div className={classNames(classes.overlay, 'whiteOverlay')}>
                    <div className={classes.titleContainer}>
                      <div className={classNames(classes.pusher, 'pusher')} />
                      <div className={classes.title}>{group.name}</div>
                    </div>
                  </div>
                </AnimatedGroupChild>
              }
              <Tappable
                component="div"
                className={classNames(classes.overlay, 'darkOverlay',
                  {
                    visible: !isCurrent,
                  })}
                onTap={animating ? undefined : () => onCardClick(group.id)}
              >
                {!isCurrent && <div className={classes.overlayHeader} />}
              </Tappable>
              {isCurrent && !animating &&
                <AnimatedGroupChild onLeave={new TimelineMax({ delay: 0.2 })}>
                  <GroupContentsExisting group={group} />
                </AnimatedGroupChild>
              }
            </AnimatedGroup>);
        },
        )}
      </CarouselRotating>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    group: {
      height: '100%',
      width: '100%',
      flexGrow: 1,
    },
    title: {
      textTransform: 'uppercase',
      color: theme.color.basis2,
      fontSize: 18,
      fontWeight: 600,
      flexGrow: 1,
      whiteSpace: 'nowrap',
      fontSmoothing: 'antialiased',
    },
    titleContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: 10,
      padding: [0, 20],
    },
    pusher: {
      flex: [1, 1, '0%'],
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme.color.white,
      zIndex: 1,
      opacity: 0,
      pointerEvents: 'none',
      cursor: 'auto !important',
      '&.visible': {
        cursor: 'pointer !important',
        opacity: 1,
        pointerEvents: 'auto',
      },
      '&.whiteOverlay': {
        backgroundColor: theme.color.white,
        opacity: 1,
      },
      '&.darkOverlay': {
        backgroundColor: theme.color.basis2,
        transition: 'opacity 0.5s',
      },
    },
    overlayHeader: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 60,
      zIndex: 2,
      opacity: 0.05,
      background: theme.color.white,
    },
  };
}

export default useSheet(applyStyles)(GroupCardsDesktopOpen);
